import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import Seo from "@/components/seo/Seo";
import { useAuth } from "@/context/AuthContext";
import { adminService } from "@/api/admin";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
    Users,
    ChartLineUp,
    CreditCard,
    Lightning,
} from "@phosphor-icons/react";

const TABS = [
    { id: "overview", label: "Overview", icon: ChartLineUp },
    { id: "users", label: "Users", icon: Users },
    { id: "predictions", label: "Predictions", icon: Lightning },
    { id: "subs", label: "Subscriptions", icon: CreditCard },
];

export default function Admin() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [tab, setTab] = useState("overview");

    useEffect(() => {
        if (loading) return;
        if (!user || !user.isAdmin) {
            navigate("/", { replace: true });
        }
    }, [user, loading, navigate]);

    if (loading || !user || !user.isAdmin) {
        return (
            <Layout withFooter={false}>
                <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center">
                    <p className="font-mono text-xs uppercase tracking-widest text-zinc-500 pulse-sharp">
                        VERIFYING ACCESS...
                    </p>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <Seo title="Admin" path="/admin" noIndex />
            <section className="max-w-7xl mx-auto px-4 md:px-8 pt-12">
                <div className="font-mono text-xs uppercase tracking-[0.4em] text-primary mb-3">
                    ▌ PLATFORM ADMIN
                </div>
                <h1 className="text-4xl md:text-5xl font-black uppercase tracking-tighter">
                    Control Room
                </h1>
                <p className="text-zinc-400 mt-3">
                    Manage users, trigger prediction runs, and inspect subscriptions.
                </p>
            </section>

            <nav className="max-w-7xl mx-auto px-4 md:px-8 mt-10 border-b border-border">
                <div className="flex flex-wrap gap-1">
                    {TABS.map((t) => {
                        const Icon = t.icon;
                        const active = tab === t.id;
                        return (
                            <button
                                key={t.id}
                                onClick={() => setTab(t.id)}
                                className={`flex items-center gap-2 px-4 py-3 font-mono text-xs uppercase tracking-widest transition-snappy ${
                                    active
                                        ? "text-primary border-b-2 border-primary -mb-px"
                                        : "text-zinc-500 hover:text-white"
                                }`}
                                data-testid={`admin-tab-${t.id}`}
                            >
                                <Icon size={14} weight="bold" />
                                {t.label}
                            </button>
                        );
                    })}
                </div>
            </nav>

            <section className="max-w-7xl mx-auto px-4 md:px-8 py-10">
                {tab === "overview" && <Overview />}
                {tab === "users" && <UsersTab />}
                {tab === "predictions" && <PredictionsTab />}
                {tab === "subs" && <SubsTab />}
            </section>
        </Layout>
    );
}

function Overview() {
    const [stats, setStats] = useState(null);
    useEffect(() => {
        adminService.stats().then(setStats).catch((err) =>
            toast.error(err?.message || "Stats failed"),
        );
    }, []);
    if (!stats) {
        return (
            <p className="font-mono text-xs uppercase tracking-widest text-zinc-500 pulse-sharp">
                LOADING...
            </p>
        );
    }
    return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="admin-overview">
            <StatGroup title="Users">
                <Cell label="Total" value={stats.users.total} />
                <Cell label="Verified" value={stats.users.verified} />
                <Cell label="VIP" value={stats.users.vip} accent />
                <Cell label="New (7d)" value={stats.users.newLast7Days} />
            </StatGroup>
            <StatGroup title="Predictions">
                <Cell label="Today" value={stats.predictions.today} />
                <Cell label="Settled (7d)" value={stats.predictions.settled7d} />
                <Cell label="Won (7d)" value={stats.predictions.won7d} accent />
                <Cell
                    label="Hit-rate (7d)"
                    value={`${stats.predictions.hitRate7d}%`}
                    accent
                />
            </StatGroup>
            <StatGroup title="Subscriptions">
                <Cell label="Successful" value={stats.subscriptions.successful} accent />
                <Cell label="Pending" value={stats.subscriptions.pending} />
            </StatGroup>
        </div>
    );
}

function UsersTab() {
    const [data, setData] = useState({ items: [], total: 0, page: 1, size: 20 });
    const [q, setQ] = useState("");
    const [page, setPage] = useState(1);

    const load = async () => {
        try {
            const res = await adminService.users({ q, page, size: 20 });
            setData(res);
        } catch (err) {
            toast.error(err?.message || "Failed to load users");
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page]);

    const onSearch = (e) => {
        e.preventDefault();
        setPage(1);
        load();
    };

    const onToggle = async (u) => {
        const nextLevel = u.userLevel === "vip" ? "basic" : "vip";
        try {
            await adminService.setLevel(u.id, nextLevel);
            toast.success(`${u.email} → ${nextLevel}`);
            load();
        } catch (err) {
            toast.error(err?.message || "Update failed");
        }
    };

    const onDelete = async (u) => {
        // Two-step confirmation so a stray tap can't nuke a user.
        if (!window.confirm(`Delete user ${u.email}? This also removes their subscription history. This cannot be undone.`)) {
            return;
        }
        try {
            const res = await adminService.deleteUser(u.id);
            toast.success(
                `Deleted ${u.email} (${res.subscriptionsDeleted} subs removed)`
            );
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || err?.message || "Delete failed");
        }
    };

    return (
        <div data-testid="admin-users">
            <form onSubmit={onSearch} className="flex gap-2 mb-4">
                <Input
                    placeholder="Search by email or name..."
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    className="rounded-none bg-background border-border"
                    data-testid="admin-users-search"
                />
                <Button
                    type="submit"
                    className="rounded-none bg-primary text-primary-foreground hover:bg-[#33FFB0] font-bold uppercase tracking-wider"
                >
                    Search
                </Button>
            </form>
            <div className="border border-border overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-card border-b border-border">
                            <Th>Email</Th>
                            <Th>Name</Th>
                            <Th>Level</Th>
                            <Th>Verified</Th>
                            <Th>Admin</Th>
                            <Th>Created</Th>
                            <Th>Actions</Th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.items.map((u) => (
                            <tr key={u.id} className="border-b border-border hover:bg-card/60">
                                <Td mono>{u.email}</Td>
                                <Td>{u.name}</Td>
                                <Td>
                                    <span
                                        className={`font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 ${
                                            u.userLevel === "vip"
                                                ? "bg-primary/10 text-primary border border-primary"
                                                : "border border-border text-zinc-300"
                                        }`}
                                    >
                                        {u.userLevel}
                                    </span>
                                </Td>
                                <Td mono>{u.isVerified ? "✓" : "—"}</Td>
                                <Td mono>{u.isAdmin ? "✓" : "—"}</Td>
                                <Td mono small>
                                    {(u.createdAt || "").slice(0, 10)}
                                </Td>
                                <Td>
                                    <div className="flex gap-2">
                                        <Button
                                            size="sm"
                                            onClick={() => onToggle(u)}
                                            disabled={u.isAdmin}
                                            className="rounded-none bg-secondary hover:bg-primary hover:text-primary-foreground font-mono text-[10px] uppercase tracking-widest"
                                            data-testid={`toggle-level-${u.id}`}
                                        >
                                            {u.userLevel === "vip" ? "Downgrade" : "Make VIP"}
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => onDelete(u)}
                                            disabled={u.isAdmin}
                                            className="rounded-none border-red-500/40 text-red-400 hover:bg-red-500 hover:text-white hover:border-red-500 font-mono text-[10px] uppercase tracking-widest"
                                            data-testid={`delete-user-${u.id}`}
                                            title={u.isAdmin ? "Admin users cannot be deleted from the panel" : "Delete user"}
                                        >
                                            Delete
                                        </Button>
                                    </div>
                                </Td>
                            </tr>
                        ))}
                        {data.items.length === 0 && (
                            <tr>
                                <td
                                    colSpan={7}
                                    className="text-center py-10 font-mono text-xs uppercase tracking-widest text-zinc-500"
                                >
                                    No users
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            <div className="flex justify-between items-center mt-4 font-mono text-xs uppercase tracking-widest text-zinc-500">
                <span>
                    Page {data.page} · {data.total} total
                </span>
                <div className="flex gap-2">
                    <Button
                        size="sm"
                        variant="outline"
                        className="rounded-none border-border"
                        disabled={page <= 1}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                        Prev
                    </Button>
                    <Button
                        size="sm"
                        variant="outline"
                        className="rounded-none border-border"
                        disabled={page * data.size >= data.total}
                        onClick={() => setPage((p) => p + 1)}
                    >
                        Next
                    </Button>
                </div>
            </div>
        </div>
    );
}

function PredictionsTab() {
    const [date, setDate] = useState("");
    const [busy, setBusy] = useState(false);
    const [lastResult, setLastResult] = useState(null);

    const run = async (kind) => {
        setBusy(true);
        try {
            let res;
            if (kind === "generate") {
                res = await adminService.generate(date || undefined);
            } else if (kind === "evaluate") {
                res = await adminService.evaluate(date || undefined);
            } else if (kind === "diagnose") {
                res = await adminService.diagnose();
            }
            setLastResult(res);
            toast.success(`${kind} done`);
        } catch (err) {
            toast.error(err?.message || `${kind} failed`);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="admin-predictions" className="max-w-2xl">
            <h2 className="text-xl font-bold tracking-tight mb-2">Trigger jobs</h2>
            <p className="text-zinc-400 text-sm mb-6">
                Manually regenerate today's slip or re-evaluate yesterday's results.
                Useful after fixture rescheduling or AI tweaks.
            </p>
            <div className="space-y-3">
                <div>
                    <label className="font-mono text-xs uppercase tracking-widest text-zinc-400 block mb-2">
                        Target date (optional · defaults to today / yesterday)
                    </label>
                    <Input
                        type="date"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        className="rounded-none bg-background border-border"
                        data-testid="admin-job-date"
                    />
                </div>
                <div className="flex gap-3">
                    <Button
                        onClick={() => run("generate")}
                        disabled={busy}
                        className="rounded-none bg-primary text-primary-foreground hover:bg-[#33FFB0] font-bold uppercase tracking-wider"
                        data-testid="admin-generate-btn"
                    >
                        {busy ? "Working..." : "Regenerate slip"}
                    </Button>
                    <Button
                        onClick={() => run("evaluate")}
                        disabled={busy}
                        variant="outline"
                        className="rounded-none border-border uppercase tracking-wider"
                        data-testid="admin-evaluate-btn"
                    >
                        Re-evaluate results
                    </Button>
                </div>
            </div>
            {lastResult && (
                <pre className="mt-6 bg-card border border-border p-4 text-xs font-mono whitespace-pre-wrap break-all">
                    {JSON.stringify(lastResult, null, 2)}
                </pre>
            )}
        </div>
    );
}

function SubsTab() {
    const [data, setData] = useState({ items: [], total: 0, page: 1, size: 20 });
    useEffect(() => {
        adminService
            .subscriptions({ page: 1, size: 20 })
            .then(setData)
            .catch((err) => toast.error(err?.message || "Failed"));
    }, []);
    return (
        <div data-testid="admin-subs">
            <div className="border border-border overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-card border-b border-border">
                            <Th>Reference</Th>
                            <Th>User</Th>
                            <Th>Amount</Th>
                            <Th>Status</Th>
                            <Th>Created</Th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.items.map((s) => (
                            <tr key={s.reference} className="border-b border-border">
                                <Td mono>{s.reference}</Td>
                                <Td mono>{s.userEmail}</Td>
                                <Td mono>
                                    ₦{Math.round((s.amountKobo || 0) / 100).toLocaleString()}
                                </Td>
                                <Td>
                                    <span
                                        className={`font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 border ${
                                            s.status === "success"
                                                ? "border-primary text-primary"
                                                : s.status === "failed"
                                                ? "border-red-500 text-red-500"
                                                : "border-amber-400 text-amber-400"
                                        }`}
                                    >
                                        {s.status}
                                    </span>
                                </Td>
                                <Td mono small>
                                    {(s.createdAt || "").slice(0, 16).replace("T", " ")}
                                </Td>
                            </tr>
                        ))}
                        {data.items.length === 0 && (
                            <tr>
                                <td
                                    colSpan={5}
                                    className="text-center py-10 font-mono text-xs uppercase tracking-widest text-zinc-500"
                                >
                                    No subscriptions yet
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            <div className="mt-4 font-mono text-xs uppercase tracking-widest text-zinc-500">
                {data.total} total
            </div>
        </div>
    );
}

function StatGroup({ title, children }) {
    return (
        <div className="bg-card border border-border p-6">
            <h3 className="text-sm font-mono uppercase tracking-widest text-zinc-500 mb-4">
                {title}
            </h3>
            <div className="space-y-3">{children}</div>
        </div>
    );
}

function Cell({ label, value, accent }) {
    return (
        <div className="flex items-baseline justify-between border-b border-border/60 pb-2">
            <span className="text-xs text-zinc-400">{label}</span>
            <span
                className={`font-mono text-xl font-bold ${accent ? "text-primary" : "text-white"}`}
            >
                {value}
            </span>
        </div>
    );
}

function Th({ children }) {
    return (
        <th className="text-left font-mono text-[10px] uppercase tracking-widest text-zinc-500 px-3 py-2">
            {children}
        </th>
    );
}

function Td({ children, mono, small }) {
    return (
        <td
            className={`px-3 py-2 ${mono ? "font-mono" : ""} ${small ? "text-xs" : "text-sm"} text-zinc-200`}
        >
            {children}
        </td>
    );
}
